'use client'

import { useMemo } from 'react'
import type { TFunction } from 'i18next'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { safeLocale } from 'src/utils/safeLocale'
import type { Translate } from './tileViewModel'

/**
 * The one way a Birdie surface gets words and numbers (B19 Task 2).
 *
 * TWO traps, both of which have already cost this widget a bug, and both of
 * which are now written down once instead of at every mount:
 *
 *  1. `useClientSideTranslation` hands back a `t` whose IDENTITY NEVER CHANGES
 *     and which reads whatever language the shared i18n instance happens to be
 *     on. A memo keyed on that `t` alone therefore keeps rendering the previous
 *     language after a runtime switch. The wrapper below pins `lng` per call and
 *     is keyed on `language`, so a switch invalidates every memo downstream.
 *  2. `en-soc` is a real language with an INVALID BCP 47 tag, and every `Intl`
 *     constructor throws a RangeError on it — which, with RUM off, takes the
 *     surface down leaving no trace. `locale` is the coerced tag, and the two
 *     formatters here are the only ones a Birdie history surface should build.
 *
 * The formatters are stable while the locale is, so they are safe memo inputs.
 */
export interface BirdieTranslate {
  language: string
  /** Language-pinned; the board's own loose `(key, options) => string` shape. */
  t: Translate
  /** The same function under i18next's branded type, for the copy factories. */
  translate: TFunction
  /** `safeLocale(language)` — the ONLY value allowed to reach `Intl`. */
  locale: string
  /** A fraction (0.7) to "70%". */
  formatPercent: (fraction: number) => string
  /** A whole number — an age, a putt length, a round number, a row's count. */
  formatInteger: (value: number) => string
}

export function useBirdieTranslate(): BirdieTranslate {
  const language = useGetCurrentLanguage()
  const { t: rawT }: { t: Translate } = useClientSideTranslation(language)

  const t = useMemo<Translate>(() => {
    const lng = language.toLowerCase()
    return (key?: string, options?: Record<string, unknown>) => rawT(key, { lng, ...options })
  }, [language, rawT])

  const locale = safeLocale(language)

  const percent = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }),
    [locale]
  )
  const integer = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }),
    [locale]
  )

  return useMemo(
    () => ({
      language,
      t,
      translate: t as unknown as TFunction,
      locale,
      formatPercent: (fraction: number) => percent.format(fraction),
      formatInteger: (value: number) => integer.format(value)
    }),
    [integer, language, locale, percent, t]
  )
}
